import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';

// Stripe promise will be initialized dynamically (separate from admin StripePaymentModal)
let customerStripePromise = null;

const PaymentForm = ({ clientSecret, onSuccess, onCancel, orderDetails, customerInfo, onPaymentCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: customerInfo?.name || '',
    email: customerInfo?.email || ''
  });

  const handleCancel = () => {
    // Show confirmation dialog for pay later
    if (confirm('Would you like to place this order and pay later instead?')) {
      if (onPaymentCancel) {
        onPaymentCancel();
      }
    } else {
      onCancel();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error('Payment system not ready. Please try again.');
      return;
    }

    if (!clientSecret) {
      toast.error('Payment information is missing. Please try again.');
      return;
    }

    setProcessing(true);
    
    console.log('🔄 Starting payment confirmation...');

    const card = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: card,
          billing_details: {
            name: billingDetails.name || 'Customer',
            email: billingDetails.email || undefined,
          },
        },
      });

      if (error) {
        console.error('❌ Stripe payment error:', error);
        let errorMessage = 'Payment failed. Please try again.';
        
        if (error.type === 'card_error' || error.type === 'validation_error') {
          errorMessage = error.message;
        } else if (error.code === 'payment_intent_authentication_failure') {
          errorMessage = 'Payment authentication failed. Please try a different card.';
        } else if (error.code === 'payment_intent_payment_attempt_failed') {
          errorMessage = 'Payment attempt failed. Please check your card details.';
        }
        
        toast.error(errorMessage);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        console.log('✅ Payment successful:', paymentIntent.id);
        toast.success('Payment successful!');
        onSuccess(paymentIntent.id);
      } else {
        console.error('❌ Unexpected payment status:', paymentIntent.status);
        toast.error('Payment processing incomplete. Please contact the restaurant.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('❌ Payment processing error:', err);
      toast.error('Payment processing error. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">Order Summary</h3>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>Restaurant:</strong> {orderDetails.restaurant || 'Restaurant'}</p>
          <p><strong>Table:</strong> {orderDetails.table || 'Table'}</p>
          <p><strong>Total Amount:</strong> {formatPrice(orderDetails.total || 0, orderDetails.currency || 'USD')}</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="customer-name" className="block text-sm font-medium text-gray-700 mb-2">
              Name (Optional)
            </label>
            <input
              type="text"
              id="customer-name"
              value={billingDetails.name}
              onChange={(e) => setBillingDetails({...billingDetails, name: e.target.value})}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your name"
            />
          </div>
          
          <div>
            <label htmlFor="customer-email" className="block text-sm font-medium text-gray-700 mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              id="customer-email"
              value={billingDetails.email}
              onChange={(e) => setBillingDetails({...billingDetails, email: e.target.value})}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your email"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Card Details
          </label>
          <div 
            className="p-3 border border-gray-300 rounded-md bg-white"
            onFocus={(e) => setTimeout(() => e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
            tabIndex={-1}
          >
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#424770',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                  invalid: {
                    color: '#9e2146',
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {processing ? 'Processing...' : `Pay ${formatPrice(orderDetails.total || 0, orderDetails.currency || 'USD')}`}
        </button>
      </div>
    </form>
  );
};

const CustomerPaymentModal = ({ isOpen, onClose, clientSecret, orderDetails, onPaymentSuccess, onPaymentCancel, apiClient, restaurantId, customerInfo }) => {
  const [stripeLoading, setStripeLoading] = useState(true);
  const [initError, setInitError] = useState(null);
  
  // Debug logging (only when modal opens)
  useEffect(() => {
    if (isOpen) {
      console.log('CustomerPaymentModal opened:', { 
        hasClientSecret: !!clientSecret, 
        hasOrderDetails: !!orderDetails, 
        restaurantId
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const initializeStripe = async () => {
      // Reset error state when modal opens
      if (isOpen) {
        setInitError(null);
        setStripeLoading(true);
      }
      
      if (!customerStripePromise && isOpen && restaurantId) {
        try {
          // Fetch restaurant's Stripe publishable key
          console.log('🔄 Fetching restaurant Stripe public key for:', restaurantId);
          const response = await apiClient.get(`/restaurant/${restaurantId}/stripe-public-key`);
          
          if (!response.data.publishable_key) {
            throw new Error('No publishable key returned from restaurant');
          }
          
          const publishableKey = response.data.publishable_key;
          console.log('✅ Got restaurant publishable key:', publishableKey?.substring(0, 20) + '...');
          
          // Validate the key format
          if (!publishableKey.startsWith('pk_')) {
            throw new Error('Invalid Stripe publishable key format');
          }
          
          customerStripePromise = loadStripe(publishableKey);
          setStripeLoading(false);
          console.log('✅ Stripe initialized successfully');
        } catch (error) {
          console.error('❌ Failed to load restaurant Stripe configuration:', error);
          let errorMessage = 'Payment system not available.';
          
          if (error.response?.status === 404) {
            errorMessage = 'Restaurant payment configuration not found. Please contact the restaurant.';
          } else if (error.response?.status === 400) {
            errorMessage = 'Restaurant payment system not properly configured.';
          } else if (error.message.includes('publishable key')) {
            errorMessage = 'Invalid payment configuration. Please contact the restaurant.';
          }
          
          setInitError(errorMessage);
          toast.error(errorMessage);
          setStripeLoading(false);
        }
      } else if (customerStripePromise && isOpen) {
        setStripeLoading(false);
      }
    };

    initializeStripe();
  }, [isOpen, apiClient, restaurantId]);

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      // Call the backend to confirm payment and update capacity (same pattern as working modal)
      await onPaymentSuccess(paymentIntentId);
      onClose();
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white sm:rounded-lg rounded-t-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Complete Payment
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {stripeLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading payment system...</span>
          </div>
        ) : initError ? (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="w-12 h-12 text-red-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-red-600 mb-4">{initError}</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        ) : customerStripePromise && clientSecret && orderDetails ? (
          <Elements stripe={customerStripePromise}>
            <PaymentForm
              clientSecret={clientSecret}
              onSuccess={handlePaymentSuccess}
              onCancel={onClose}
              onPaymentCancel={onPaymentCancel}
              orderDetails={orderDetails}
              customerInfo={customerInfo}
            />
          </Elements>
        ) : (
          <div className="text-center py-8">
            <div className="mb-4">
              <svg className="w-12 h-12 text-yellow-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-gray-700 mb-4">
              {!clientSecret ? 'Payment information is missing.' : 
               !orderDetails ? 'Order details are missing.' :
               'Payment system not available.'}
            </p>
            <p className="text-sm text-gray-500 mb-4">Please contact the restaurant for assistance.</p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerPaymentModal;